import { action, Action, ActionOn, actionOn, thunk, Thunk } from 'easy-peasy';
import DropQuery, { FEATURED_SHOULD_FILTER, shoudFilter } from 'Features/Query/DropsQuery';
import { omit } from 'lodash';
import { TFilter } from 'Models/App/@types';
import DropModel from 'Models/Drop';
import { TDrop, TDropSearchResponse } from 'Models/Drop/@types';
import { TEvent } from 'Models/Event';
import { EVENT_FIELDS } from 'Models/Event/utils';
import { resourceIncludeFilter } from 'Models/Resource';
import { TRootStore } from 'Stores';

export const DROP_LIST_LIMIT = 20;

export interface TCommunityDropsState {
  isLoading: boolean;
  setIsLoading: Action<TCommunityDropsState, boolean>;

  total: number;
  aggregations?: Record<string, unknown>;

  drops: TDrop[];
  setDropList: Action<TCommunityDropsState, { drops: TDrop[] }>;

  hasMore: boolean;
  setHasMore: Action<TCommunityDropsState, boolean>;

  isFeaturedPresent: boolean;
  setIsFeaturedPresent: Action<TCommunityDropsState, boolean>;

  featuredDrops: TDrop[];

  fetchDrops: Thunk<TCommunityDropsState, { id: string; query: DropQuery; page?: number }>;

  onContentFavorite: ActionOn<TCommunityDropsState, TRootStore>;
  onContentUnFavorite: ActionOn<TCommunityDropsState, TRootStore>;
  onEventRemind: ActionOn<TCommunityDropsState, TRootStore>;
  onEventUnRemind: ActionOn<TCommunityDropsState, TRootStore>;
}

const DROPS_SEARCH_PAGE_LIMIT = 20;

const CommunityDropsStore: TCommunityDropsState = {
  isLoading: false,
  setIsLoading: action((state, payload) => {
    state.isLoading = payload;
  }),

  total: 0,
  aggregations: {},

  drops: [],
  setDropList: action((state, payload) => {
    state.drops = [...state.drops, ...payload.drops];
  }),

  hasMore: true,
  setHasMore: action((state, payload) => {
    state.hasMore = payload;
  }),

  isFeaturedPresent: true,
  setIsFeaturedPresent: action((state, payload) => {
    state.isFeaturedPresent = payload;
  }),

  featuredDrops: [],

  fetchDrops: thunk(async (actions, { id, query, page = 0 }, { getState }) => {
    actions.setIsLoading(true);
    if (page === 0) {
      getState().isFeaturedPresent = true;
      getState().featuredDrops = [];
      getState().drops = [];
    }
    const state = getState();
    if (getState().isFeaturedPresent) {
      const getFeaturedDrops = await getDrops(undefined, {
        filter: {
          limit: DROPS_SEARCH_PAGE_LIMIT,
          skip: page * DROPS_SEARCH_PAGE_LIMIT,
          ...query.featuredSearchFilter,
          ...TDropFilters(id, true),
        },
      });
      state.total = getFeaturedDrops.total;
      state.aggregations = getFeaturedDrops.aggregations;
      state.drops = [...state.drops, ...getFeaturedDrops.results];
      if (getFeaturedDrops.results.length < DROPS_SEARCH_PAGE_LIMIT) {
        state.featuredDrops = [...state.drops];
        state.isFeaturedPresent = false;
        const getUnfeaturedDrops = await getDrops(undefined, {
          filter: {
            limit: DROPS_SEARCH_PAGE_LIMIT,
            skip: 0,
            ...query.searchFilter,
            ...TDropFilters(id),
          },
        });
        state.total = getUnfeaturedDrops.total;
        state.aggregations = getUnfeaturedDrops.aggregations;
        state.drops = [...state.drops, ...getUnfeaturedDrops.results];
        actions.setHasMore(!(getUnfeaturedDrops.results.length < DROPS_SEARCH_PAGE_LIMIT));
      }
    } else {
      const getUnfeaturedDrops = await getDrops(undefined, {
        filter: {
          limit: DROPS_SEARCH_PAGE_LIMIT,
          skip: state.drops.length - state.featuredDrops.length ?? 0,
          ...query.searchFilter,
          ...TDropFilters(id),
        },
      });
      state.total = getUnfeaturedDrops.total;
      state.aggregations = getUnfeaturedDrops.aggregations;
      state.drops = [...state.drops, ...getUnfeaturedDrops.results];
      actions.setHasMore(!(getUnfeaturedDrops.results.length < DROPS_SEARCH_PAGE_LIMIT));
    }
    actions.setIsLoading(false);
  }),

  onContentFavorite: actionOn(
    (actions, storeActions) => storeActions.AuthStore.contentFavorited,
    (state, { payload: { event, favouriteId } }) => {
      state.drops = state.drops.map((e) => (e.id === event.id ? { ...e, ...event, isFavourited: true, favouriteId } : e)) as TDrop[];
    },
  ),
  onContentUnFavorite: actionOn(
    (actions, storeActions) => storeActions.AuthStore.contentUnFavorited,
    (state, { payload }) => {
      state.drops = state.drops.map((e) => (e.id === payload ? omit(e, ['isFavourited', 'favouriteId']) : e)) as TDrop[];
    },
  ),
  onEventRemind: actionOn(
    (actions, storeActions) => storeActions.AuthStore.eventReminded,
    (state, { payload: { event, reminderId } }) => {
      state.drops = state.drops.map((e) => (e.id === event.id ? { ...e, ...event, isReminded: true, reminderId } : e));
    },
  ),
  onEventUnRemind: actionOn(
    (actions, storeActions) => storeActions.AuthStore.eventUnReminded,
    (state, { payload }) => {
      state.drops = state.drops.map((e) => (e.reminderId === payload ? omit(e, ['isReminded', 'reminderId']) : e)) as TDrop[];
    },
  ),
};

const getDrops = async (id: string | undefined, filters: TFilter): Promise<TDropSearchResponse> => {
  const featuredDrops = await DropModel.search(id, filters);
  return featuredDrops;
};

export const TDropFilters = (id: string, isFeatured?: boolean) => ({
  Event: {
    fields: EVENT_FIELDS,
    include: [
      'tags',
      'ticketingInfo',
      {
        relation: 'community',
        scope: {
          fields: ['_profileImages', 'id', 'name', 'slug'],
        },
      },
      'includePurchasedTickets',
      // 'includePurchasersMeta',
      'collaborators',
    ],
  },
  Video: resourceFilter(id, isFeatured),
  Collect: resourceFilter(id, isFeatured),
  Podcast: resourceFilter(id, isFeatured),
  Article: resourceFilter(id, isFeatured),
  Album: resourceFilter(id, isFeatured),
  Soundtrack: resourceFilter(id, isFeatured),
});

export default CommunityDropsStore;

export const resourceFilter = (id: string, isFeatured?: boolean) => {
  const _filter = isFeatured
    ? { should: [shoudFilter(id), FEATURED_SHOULD_FILTER] }
    : { should: [shoudFilter(id)], where: { isUserFeatured: false, featured: false } };
  return {
    ..._filter,
    include: [
      {
        relation: 'community',
        scope: {
          fields: ['name', '_profileImages'],
        },
      },
      {
        relation: 'tags',
        scope: {
          fields: ['name', 'background', 'color'],
        },
      },
      'collaborators',
    ],
  };
};
